import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { useRootStore } from "store";
import { Icon, SortIcon } from "components/icon/Icon";
import { Loader } from "components/loader/Loader";
import { MyCustomer } from "models/customers/customers";
import { getActivityFromCustomer } from "common/customer/getActivityFromCustomer";
import {
  pluralizeNewDeliveries,
  pluralizeNewMessages
} from "common/pluralize/pluralize";
import { SortOrder } from "common/SortOrder";
import CustomersHeader from "views/customers/components/CustomersHeader";

import "../containers/MyCustomers.scss";

export default function MyCustomers() {
  const navigate = useNavigate();

  const { setWorkingCustomerId } = useRootStore((state) => state.global);

  const { myCustomers, fetchCustomer } = useRootStore(
    (state) => state.customers
  );

  const { customers, isLoading: isLoadingCustomers } = myCustomers;

  const SortCustomerName = (customer: MyCustomer) => customer.customerName;
  const SortMessages = (customer: MyCustomer) =>
    customer.messagesSummary?.unreadCount ?? 0;
  const SortDeliveries = (customer: MyCustomer) =>
    customer.filesSummary?.unhandledCount ?? 0;

  const SortByDefault = (customer: MyCustomer) => {
    if (customer) {
      const fileResult =
        (customer.filesSummary?.unhandledCount &&
          customer.filesSummary.unhandledCount > 0 &&
          customer.filesSummary?.lastFileDateTime &&
          new Date(customer.filesSummary.lastFileDateTime).getTime()) ||
        0;

      const messageResult =
        (customer.messagesSummary?.unreadCount &&
          customer.messagesSummary.unreadCount > 0 &&
          customer.messagesSummary?.lastMessageDateTime &&
          new Date(customer.messagesSummary.lastMessageDateTime).getTime()) ||
        0;

      return Math.max(fileResult, messageResult);
    }
    return 0;
  };

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortOrder, setSortOrder] = useState(SortOrder.Descending);
  const [sortBy, setSortBy] = useState("");

  const handleOnWriteMessage = (customerId: string) => {
    setWorkingCustomerId(customerId);
    fetchCustomer(customerId);
    navigate(`/customers/${customerId}/inbox/?editing=true`);
  };

  const isMissingSearchResults = (): boolean => {
    return !!searchTerm && !isLoadingCustomers && customers.length === 0;
  };

  const setSorting = (newSortBy: string): void => {
    const newSortOrder =
      sortBy === newSortBy && sortOrder === SortOrder.Ascending
        ? SortOrder.Descending
        : SortOrder.Ascending;
    setSortOrder(newSortOrder);
    setSortBy(newSortBy);
  };

  const sortTable = (customerA: MyCustomer, customerB: MyCustomer) => {
    let sortFunction: (customer: MyCustomer) => number | string = SortByDefault;

    switch (sortBy) {
      case "SortDeliveries":
        sortFunction = SortDeliveries;
        break;
      case "SortMessages":
        sortFunction = SortMessages;
        break;
      case "SortCustomerName":
        sortFunction = SortCustomerName;
        break;
      default:
        sortFunction = SortByDefault;
        break;
    }

    if (sortOrder === SortOrder.Descending) {
      return sortFunction(customerA) > sortFunction(customerB) ? -1 : 1;
    }

    return sortFunction(customerA) > sortFunction(customerB) ? 1 : -1;
  };

  return (
    <div className={"box"}>
      <CustomersHeader setSearchTerm={setSearchTerm} />
      <div className="table-wrapper w-100">
        <table className="w-100 min-w-400 my-customers-table">
          <thead>
            <tr>
              <th
                className="cursor-pointer"
                style={{ minWidth: "200px" }}
                onClick={() => setSorting("SortCustomerName")}
              >
                <div className="d-flex">
                  Kund{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={sortBy === "SortCustomerName" ? sortOrder : ""}
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortMessages")}
              >
                <div className="d-flex">
                  Meddelanden{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={sortBy === "SortMessages" ? sortOrder : ""}
                  />
                </div>
              </th>
              <th
                className="cursor-pointer"
                onClick={() => setSorting("SortDeliveries")}
              >
                <div className="d-flex">
                  Inleveranser{" "}
                  <SortIcon
                    className="ml-2"
                    sortOrder={sortBy === "SortDeliveries" ? sortOrder : ""}
                  />
                </div>
              </th>
              <th>Kundaktivitet</th>
            </tr>
          </thead>
          <tbody>
            {customers
              .filter((customer) => {
                return (
                  !searchTerm ||
                  customer.customerName
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()) ||
                  customer.organizationNumber.includes(searchTerm) ||
                  customer.customerNumber.includes(searchTerm)
                );
              })
              .sort(sortTable)
              .map((customer) => {
                const messageText = pluralizeNewMessages(
                  customer.messagesSummary?.unreadCount || 0
                );

                const documentText = pluralizeNewDeliveries(
                  customer.filesSummary?.unhandledCount || 0
                );
                const activity = getActivityFromCustomer(
                  customer.lastAccessExternal
                );
                const activityName = customer.lastAccessExternalName;

                return (
                  <tr key={customer.customerNumber}>
                    <td>
                      <div>
                        <Link
                          to={`/customers/${customer.customerId}/dashboard`}
                          title="Klicka för att gå till kund"
                        >
                          <b>{customer.customerName}</b>
                        </Link>
                        <Icon
                          className="ml-3"
                          name="envelope-o"
                          onClick={() =>
                            handleOnWriteMessage(customer.customerId)
                          }
                          title="Klicka för att gå till kund och skicka ett meddelande"
                        />
                      </div>
                      <div>{customer.customerNumber}</div>
                    </td>
                    <td>
                      <Link to={`/customers/${customer.customerId}/inbox`}>
                        {messageText}
                      </Link>
                    </td>
                    <td>
                      <Link to={`/customers/${customer.customerId}/delivery/`}>
                        {documentText}
                      </Link>
                    </td>
                    <td>
                      <div className="customerListCustomerName">
                        <span className={"customerName"}>{activityName}</span>
                      </div>
                      <div>{activity}</div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {isMissingSearchResults() && <p>Du har inga kunder för tillfället.</p>}

      {isLoadingCustomers && <Loader className="p-4" />}
    </div>
  );
}
